import clsx from 'clsx'
import { ReactNode, InputHTMLAttributes, TextareaHTMLAttributes } from 'react'

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-theme-text-subtle">
          {label}
          {required && <RequiredStar />}
        </span>
        {error ? (
          <span className="text-xs text-theme-error">{error}</span>
        ) : hint ? (
          <span className="text-xs text-theme-text-muted">{hint}</span>
        ) : null}
      </div>
      {children}
    </label>
  )
}

function RequiredStar() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-primary-400"
      aria-label="required"
    >
      <path d="M11.074 2.633c.32-.844 1.531-.844 1.852 0l2.07 5.734c.145.38.514.633.926.633h5.087c.94 0 1.35 1.17.611 1.743L18 14a.968.968 0 0 0-.322 1.092L19 20.695c.322.9-.72 1.673-1.508 1.119l-4.917-3.12a1 1 0 0 0-1.15 0l-4.917 3.12c-.787.554-1.83-.22-1.508-1.119l1.322-5.603A.968.968 0 0 0 6 14l-3.62-3.257C1.64 10.17 2.052 9 2.99 9h5.087a.989.989 0 0 0 .926-.633l2.07-5.734z" />
    </svg>
  )
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean
}

export function TextInput({ invalid, className, ...props }: TextInputProps) {
  return (
    <input
      {...props}
      aria-invalid={invalid || undefined}
      className={clsx(
        'block w-full rounded-xl bg-theme-input-bg px-3.5 py-3 text-[15px] text-white placeholder:text-theme-text-muted',
        'ring-1 focus:outline-none',
        invalid
          ? 'ring-theme-error focus:ring-theme-error'
          : 'ring-theme-input-border focus:ring-primary-300',
        className,
      )}
    />
  )
}

export function TextArea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={clsx(
        'block w-full rounded-xl bg-theme-input-bg px-3.5 py-3 text-[15px] text-white placeholder:text-theme-text-muted',
        'ring-1 ring-theme-input-border focus:outline-none focus:ring-primary-300',
        'min-h-[96px] resize-y',
        className,
      )}
    />
  )
}
